import React, {FC} from 'react';
import styled from 'styled-components';
import {Handler} from '../../../lib/typeHelpers';
import {fontFamiliesR4, paletteR4} from '../../../lib/styleVariablesR4';
import {mediaBreakpointDown, transition} from '../../../lib/styleVariables';
import {PrimaryButton} from '../../molecules/buttonsR4';
import {convertHexToRGBA} from '../../../lib/color-utils';
import {Overlay} from '../../molecules/overlay';
import {Icon} from '../../molecules/icon';
import {SubscriptionForm, SubscriptionFormData} from './subscriptionForm';
import useHubspotForm from '../../../lib/hooks/useHubspotForm';

interface OverlayProps {
  isOpen: boolean;
  id: string;
  onClose: Handler;
  formData: SubscriptionFormData;
  successTitle: string;
  regularTitle: string;
  backLinkLabel: string;
  buttonLabel: string;
  inputPlaceholder: string;
  shouldSubmitOnOpen?: boolean;
  email?: string;
}

const StyledInnerOverlay = styled.div`
  width: 100%;
  height: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledTitle = styled.h2`
  margin-bottom: 48px;
  font-family: ${fontFamiliesR4.frontPageTitle};
  font-size: 40px;
  line-height: 48px;
  text-align: center;
  letter-spacing: -0.01em;
  font-feature-settings: 'salt' on;
  color: ${paletteR4.white.base};

  ${mediaBreakpointDown('tablet')} {
    margin-bottom: 24px;
    font-size: 24px;
    line-height: 30px;
  }
`;

const StyledBackLink = styled(PrimaryButton)`
  background: ${paletteR4.white.base};
  color: ${paletteR4.red.base};

  &:hover {
    color: ${paletteR4.white.base};
    background: ${paletteR4.red.base};
  }
`;

const StyledCloseIcon = styled.button`
  padding: 8px;
  background: none;
  border: none;
  border-radius: 8px;
  position: absolute;
  right: 5%;
  top: 5%;
  cursor: pointer;
  transition: ${transition.fast};

  &:hover {
    background: ${convertHexToRGBA(paletteR4.white.base, 0.1)};
  }
`;

const StyledFormWrapper = styled.div<{isHidden?: boolean}>`
  width: 100%;
  display: flex;
  justify-content: center;
  height: ${props => (props.isHidden ? 0 : 'auto')};
  opacity: ${props => (props.isHidden ? 0 : 1)};
  overflow: ${props => (props.isHidden ? 'hidden' : 'auto')};
`;

const backdropStyle = `
  background: ${paletteR4.black.base};
  opacity: 0.95;
`;

export const SubscriptionOverlay: FC<OverlayProps> = props => {
  const {
    isOpen,
    onClose,
    formData,
    successTitle,
    regularTitle,
    backLinkLabel,
    buttonLabel,
    inputPlaceholder,
    shouldSubmitOnOpen,
    id,
    email
  } = props;

  const [isSubmitted, setIsSubmitted] = React.useState(shouldSubmitOnOpen);
  const nativeFormContainerRef = React.useRef<HTMLDivElement>();

  const submitSubscriptionForm = useHubspotForm(id, formData, nativeFormContainerRef);

  const handleSubmit = (inputValue: string) => {
    submitSubscriptionForm(inputValue);
    setIsSubmitted(true);
  };

  const handleClose = () => {
    onClose();
  };

  React.useEffect(() => {
    if (shouldSubmitOnOpen && email)
      submitSubscriptionForm(email);
  }, [shouldSubmitOnOpen, email]);

  return (
    <Overlay backdropCss={backdropStyle} open={isOpen} onClose={handleClose}>
      <StyledInnerOverlay>
        <StyledTitle>{isSubmitted ? successTitle : regularTitle}</StyledTitle>

        {isSubmitted && <StyledBackLink onClick={handleClose}>{backLinkLabel}</StyledBackLink>}

        <StyledFormWrapper isHidden={isSubmitted}>
          <SubscriptionForm
            buttonLabel={buttonLabel}
            inputPlaceholder={inputPlaceholder}
            onSubmit={handleSubmit}
          />
        </StyledFormWrapper>

        <StyledCloseIcon onClick={handleClose}>
          <Icon name="exitFrontPage" size={16} color={paletteR4.white.base} useR4 />
        </StyledCloseIcon>

        <div ref={nativeFormContainerRef as any} className="visually-hidden" />
      </StyledInnerOverlay>
    </Overlay>
  );
};
